/* eslint-disable react-hooks/exhaustive-deps */
import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTable, useSortBy } from 'react-table';
import { List } from 'react-virtualized';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import ModuleExport from '../ModuleExport';
import {
  downloadReportFunc,
  exportReportWithHeaders
} from '../../utils/export';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import numeral from 'numeral';

const Table = ({
  data,
  headerFormatter,
  defaultSorts,
  view,
  filters,
  queryMeta,
  width,
  height,
  rowHeight,
  showRank,
  titleName,
  dataSize,
  fetchData,
  csv2xls
}) => {
  const rowData = useMemo(() => data, [data]);
  const columns = useMemo(() => headerFormatter, [headerFormatter]);

  const initialState = {};
  if (defaultSorts.length) {
    initialState.sortBy = defaultSorts.map(id => ({ id, desc: true }));
  }

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow
  } = useTable(
    {
      columns,
      data: rowData,
      initialState
    },
    useSortBy
  );

  const loadingRowRenderer = useCallback(
    ({ key, style }) => {
      return (
        <div
          style={style}
          key={key}
          className="tr tvt-tabular-data-table__loading-row"
        >
          {headerFormatter.map(h => (
            <div key={`td-${h.accessor}`} className={`td ${h.className}`}>
              <div className="tvt-tabular-data-table__loading-bar" />
            </div>
          ))}
        </div>
      );
    },
    [headerFormatter]
  );

  const rowRenderer = useCallback(
    ({ index, key, style }) => {
      const row = rows[index];
      prepareRow(row);
      return (
        <div style={style} key={key} className="tr">
          {row.cells.map((cell, i) => {
            if (
              ['movie', 'show', 'title', 'name'].includes(cell.column.id) &&
              cell.value.length > 100
            ) {
              cell.value = (
                <span data-toggle="tooltip" title={cell.value}>
                  {cell.value.substring(0, 100) + '...'}
                </span>
              );
            }
            return i === 0 && showRank ? (
              <div
                key={`cell-${cell.row.id}-${cell.value}`}
                className={`td ${cell.column.className ||
                  'tvt-tabular-data-table__item'}`}
              >
                {index + 1}
              </div>
            ) : (
              <div
                {...cell.getCellProps()}
                className={`td ${cell.column.className ||
                  'tvt-tabular-data-table__item'}`}
              >
                {cell.render('Cell')}
              </div>
            );
          })}
        </div>
      );
    },
    [prepareRow, rows]
  );

  const renderInfoIconWithTooltip = ({ message, placement }) => {
    return (
      <OverlayTrigger
        placement={placement}
        overlay={
          <Tooltip id="tvt-tabular-data-table-tooltip">{message}</Tooltip>
        }
      >
        <i className="tvt-tabular-data-table__tooltip fa fa-info-circle" />
      </OverlayTrigger>
    );
  };

  const renderSortIcon = column => {
    if (!column.isSorted) {
      return null;
    }

    return column.isSortedDesc ? (
      <FontAwesomeIcon
        className="tvt-tabular-data-table__sort-icon"
        icon="caret-down"
        size="1x"
      />
    ) : (
      <FontAwesomeIcon
        className="tvt-tabular-data-table__sort-icon"
        icon="caret-up"
        size="1x"
      />
    );
  };

  return (
    <div className="tvt-tabular-data-table__container">
      <ModuleExport
        fetchData={fetchData}
        data={exportReportWithHeaders(data)}
        tableDataDownload={
          fetchData ? null : downloadReportFunc(view, filters, queryMeta)
        }
        showTitle={titleName}
        tableClassName="cvmi-table__moviesSummary"
        csv2xls
        csv
        xls
        png
      />
      <div {...getTableProps()} className="tvt-tabular-data-table">
        <div className="tvt-tabular-data-table__header">
          {dataSize > 0 && (
            <div className="tvt-tabular-data-table__header-data-size">
              {' '}
              {`N = ${numeral(dataSize).format(',')}`}{' '}
            </div>
          )}
          {headerGroups.map(headerGroup => (
            <div {...headerGroup.getHeaderGroupProps()} className="tr">
              {headerGroup.headers.map(column => (
                <div
                  {...column.getHeaderProps(
                    column.getSortByToggleProps({
                      title: column.hoverTitle || 'Sort'
                    })
                  )}
                  className={`th ${column.className ||
                    'tvt-tabular-data-table__item'}`}
                >
                  {column.render('Header')}
                  {column.tooltip && renderInfoIconWithTooltip(column.tooltip)}
                  {renderSortIcon(column)}
                </div>
              ))}
            </div>
          ))}
        </div>
        <div {...getTableBodyProps()}>
          {data.length < 1 ? (
            <List
              className="tvt-tabular-data-table__loading"
              rowCount={Math.round(height / rowHeight)}
              width={width}
              height={height}
              rowHeight={rowHeight}
              rowRenderer={loadingRowRenderer}
            />
          ) : (
            <List
              rowCount={rows.length}
              width={width}
              height={height}
              rowHeight={rowHeight}
              rowRenderer={rowRenderer}
              overscanRowCount={3}
            />
          )}
        </div>
      </div>
    </div>
  );
};

Table.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  headerFormatter: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  width: PropTypes.number,
  height: PropTypes.number,
  rowHeight: PropTypes.number,
  defaultSorts: PropTypes.arrayOf(PropTypes.string),
  view: PropTypes.string.isRequired,
  filters: PropTypes.shape({}).isRequired,
  queryMeta: PropTypes.shape({}).isRequired,
  showRank: PropTypes.bool,
  dataSize: PropTypes.number
};

Table.defaultProps = {
  width: 1160,
  height: 600,
  rowHeight: 50,
  defaultSorts: [],
  showRank: true
};

export default Table;
